import React from 'react';
import { IonIcon } from '@ionic/react';
import { imageOutline, videocamOutline } from 'ionicons/icons';
import { X } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import VideoJS from '@/components/VideoJS';
import HorizontalScrollMedia from '@/components/HorizontalScrollMedia';
import { MediaItem } from '@/hooks/post/usePostForm';

type MediaGalleryProps = {
  media: MediaItem[];
  mediaDimensions: { [key: number]: { width: number; height: number } };
  onImageLoad: (index: number, event: React.SyntheticEvent<HTMLImageElement>) => void;
  onDelete: (index: number) => void;
};

export function MediaGallery({ media, mediaDimensions, onImageLoad, onDelete }: MediaGalleryProps) {
  if (media.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full overflow-x-auto scroll-smooth space-x-2 mb-4 hide-scrollbar">
      <HorizontalScrollMedia>
        {media.map((item, index) => {
          const dimensions = mediaDimensions[index];
          const aspectRatio = dimensions ? dimensions.width / dimensions.height : 1;

          if (item.type === 'video') {
            return (
              <div
                key={`video-${index}`}
                className="relative rounded-lg overflow-hidden"
              >
                {item.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Spinner />
                  </div>
                )}
                <div
                  className="min-w-64 max-h-[512px] video-wrapper rounded-lg overflow-hidden"
                >
                  {item.url.startsWith('data:') ? (
                    <VideoJS
                      options={{
                        autoplay: false,
                        playsinline: true,
                        controls: false,
                        preload: 'auto',
                        loop: true,
                        fluid: true,
                        fill: false,
                        responsive: true,
                        sources: [{
                          src: item.url,
                          type: item.file?.type || 'video/mp4',
                        }],
                      }}
                    />
                  ) : item.url.startsWith('file://') ? (
                    // For file:// URLs, we need to use a different approach
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                      <div className="text-center p-2">
                        <IonIcon icon={videocamOutline} className="text-gray-500 text-4xl" />
                        <p className="text-xs text-gray-500 mt-1">Video from shared media</p>
                      </div>
                    </div>
                  ) : (
                    <VideoJS
                      options={{
                        autoplay: false,
                        playsinline: true,
                        controls: false,
                        preload: 'auto',
                        loop: true,
                        fluid: true,
                        fill: false,
                        responsive: true,
                        sources: [{
                          src: item.url,
                          type: item.file?.type || 'video/mp4',
                        }],
                      }}
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="absolute right-2 top-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(index);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          } else {
            return (
              <div
                key={`image-${index}`}
                className="relative h-48 shrink-0 cursor-pointer md:max-w-[300px] max-w-[412px] bg-gray-100 rounded-lg overflow-hidden"
                style={{ width: `${aspectRatio * 12}rem` }}
              >
                {item.url.startsWith('data:') ? (
                  <img
                    src={item.url}
                    alt={`Preview ${index + 1}`}
                    onLoad={(e) => onImageLoad(index, e)}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : item.url.startsWith('file://') ? (
                  // For file:// URLs, we need to use a different approach
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                    <div className="text-center p-2">
                      <IonIcon icon={imageOutline} className="text-gray-500 text-4xl" />
                      <p className="text-xs text-gray-500 mt-1">Image from shared media</p>
                    </div>
                  </div>
                ) : (
                  <img
                    src={item.url}
                    alt={`Preview ${index + 1}`}
                    onLoad={(e) => onImageLoad(index, e)}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}

                {item.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <Spinner />
                  </div>
                )}

                <button
                  type="button"
                  className="absolute right-2 top-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(index);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          }
        })}
      </HorizontalScrollMedia>
    </div>
  );
}
